//
//  levels.ts
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 24, 2026
//  Author: Bob Keathley
//
//  Types, paths and helpers for level Notebooks, teams, people and effective
//  Notebooks (spec §6v, SIG-168): the wire shapes the Hierarchy, Level, Person
//  and Project pages read, the signer rules, and the refusal wording.
//
import { atLeast } from '../auth'
import type { AiAccount, Profile, ProfileBody } from './people'

export type NotebookRef = { notebook_id: string; version: number }
export type Person = { user_id: string; email: string; name: string }
export type TreeProject = { id: string; name: string; people: Person[]; notebook: NotebookRef | null }
export type TreeTeam = { id: string; name: string; notebook: NotebookRef | null; people: Person[]; projects: TreeProject[] }
export type LevelTree = {
  corporate: { notebook: NotebookRef | null; people: Person[] }
  teams: TreeTeam[]
  unassigned_projects: TreeProject[]
}

export type PersonRow = {
  user_id: string
  email: string
  name: string
  role: string
  attachment: { level: 'corporate' | 'team' | 'project'; ref_id: string | null; ref_name: string | null } | null
  user_notebook: NotebookRef | null
}

export type LevelKind = 'corporate' | 'team' | 'project' | 'user'
/** `source` is null for a rule the provenance does not cover; `origin` is the server's own label. */
export type LevelSource = { key: string; source: string | null; origin?: string | null; value?: unknown }
export type AdvisoryItem = { source: string; scope: string; depth: number | null; path_rel: string; sha256: string; text: string }
export type LevelNotebook = {
  notebook_id: string | null
  level: LevelKind
  ref_id: string | null
  version: number | null
  birth_cert_id: string | null
  compiled_hash: string | null
  compiled_mandate: Record<string, unknown> | null
  sources: LevelSource[]
  advisory: AdvisoryItem[]
  unassigned: unknown[]
  signer: { user_id: string; at: number } | null
  /** User Notebooks only (§6w): the profile version sealed into this version. */
  profile_version?: number | null
}
export type LevelPreview = {
  compiled_mandate: Record<string, unknown>
  compiled_hash: string
  sources: LevelSource[]
  advisory: AdvisoryItem[]
  unassigned: unknown[]
  next_version: number
  unchanged: boolean
  can_sign: boolean
  cannot_sign_reason: 'own_notebook' | 'role' | null
}
export type LevelBirth = { notebook_id: string; version: number; birth_cert_id: string; stale_marked: number }
export type SentBack = { reason: string; by: string | { user_id: string; name: string | null } | null; at: number }
export type LevelVersion = {
  version: number; birth_cert_id: string; compiled_hash: string; signed_by: string | { user_id: string; name: string | null } | null; signed_at: number; source: string
  self_signed?: boolean; status?: 'in_effect' | 'superseded' | 'sent_back'; sent_back?: SentBack | null
}
export type SendBackResult = { notebook_id: string; sent_back_version: number; in_effect_version: number | null; stale_marked: number }
/** `GET /levels/users/me`: the caller's own Person page, attached to a level or not (§6v amendment). */
export type PersonMe = {
  user_id: string
  email: string
  name: string
  role: string
  attachment: PersonRow['attachment']
  notebook: LevelNotebook
  versions: LevelVersion[]
  sent_back: (SentBack & { version: number }) | null
  /** The current profile itself (§6w); older builds sent the `{current}` body, which `normalizeProfile` also accepts. */
  profile: Profile | ProfileBody | null
  ai_accounts: AiAccount[]
  can_sign: boolean
}

export type EffectiveParent = { level: string; notebook_id: string; version: number; birth_cert_id: string }
export type EffectiveEntry = { id: string; agent: string; role: 'agent' | 'user_session'; birth_cert_id: string; compiled_hash: string; stale: boolean; built_at: number; parents: EffectiveParent[] }
export type EffectivePerson = { user_id: string; email: string; name: string; has_user_notebook?: boolean; entries: EffectiveEntry[] }
export type Effective = { people: EffectivePerson[] }
export type Rebirth = { rebuilt: { id: string; user_id: string; agent: string }[]; skipped: { user_id: string; agent: string; reason: string }[]; count: number }

/** `{node}` of the level routes: `corporate`, `teams/{id}`, `projects/{id}` or `users/{id}`. */
export const nodePath = (kind: LevelKind, id?: string | null) => (kind === 'corporate' ? 'corporate' : `${kind === 'user' ? 'users' : `${kind}s`}/${id}`)
export const notebookPath = (node: string) => `/levels/${node}/notebook`
export const previewPath = (node: string) => `/levels/${node}/notebook/preview`
export const versionsPath = (node: string) => `/levels/${node}/notebook/versions`
export const mePath = '/levels/users/me'
export const effectivePath = (projectId: string) => `/projects/${projectId}/effective`
export const rebirthAffectedPath = (projectId: string) => `/projects/${projectId}/effective/rebirth-affected`

/** Corporate is signed by owner or security-admin; Team, Project and User Notebooks by team-admin and above (§6v). */
export const canSignLevel = (role: string | undefined, kind: LevelKind) => atLeast(role, kind === 'corporate' ? 'security-admin' : 'team-admin')
/** Teams, attachments and re-birth are team-admin+. */
export const canEditHierarchy = (role: string | undefined) => atLeast(role, 'team-admin')

export const levelLabel = (k: string) => (k === 'corporate' ? 'Corporate' : k === 'team' ? 'Team' : k === 'project' ? 'Project' : k === 'user' ? 'User' : k === 'canon' ? 'Canon' : k)

/** The scanned file or the answer a rule came from, in plain words. */
export function sourceLabel(source: string | null | undefined): string {
  if (!source) return 'no source recorded'
  if (source.startsWith('level:')) return 'set at this level'
  if (source.startsWith('intake:')) return `answer: ${source.slice('intake:'.length)}`
  if (source.startsWith('import:')) { const [, scope, ...rest] = source.split(':'); return `${scope} file ${rest.join(':')}` }
  return source
}

/** Human text for the §6v refusals; falls back to the server's message. */
export function levelErrorText(e: { code: string; message: string; details?: unknown }): string {
  const d = (e.details ?? {}) as { code?: string; reason?: string }
  const sub = d.code ?? d.reason
  if (e.code === 'own_notebook') return 'You cannot send back your own Notebook.'
  if (e.code === 'assumed_read_only') return 'This assumed session is read-only.'
  if (e.code === 'assumed_no_grant') return 'This assumed session has no grant to sign.'
  if (e.code === 'forbidden') return 'Your role may not do this.'
  if (sub === 'no_soul_md') return 'Waiting for a profile from the tray (My Notebook → Draft with Claude). Sign after it arrives.'
  if (sub === 'nothing_to_send_back') return 'There is no signed version in effect to send back.'
  if (sub === 'reason_required') return 'A reason is required.'
  if (sub === 'no_change') return 'Nothing changed: this gives the same Notebook as the current version, so nothing was signed.'
  if (sub === 'name_taken') return 'A team with that name already exists.'
  if (sub === 'team_not_empty') return 'The team still has projects or people. Move them first.'
  if (sub === 'answers_not_allowed') return 'This level takes no answers.'
  if (sub === 'bad_attachment') return 'That attachment is not valid.'
  if (e.code === 'kek_unavailable') return 'The tenant signing key is not configured. Nothing was signed.'
  return e.message
}
